import { GameAudio } from "./browser/audio";
import {
  analytics,
  beginAnalyticsRun,
  type AnalyticsParams,
} from "./browser/analytics";
import { spriteMarkup } from "./browser/assets";
import { InputController } from "./browser/input";
import { musicStateForBattle, soundtrack } from "./browser/music";
import {
  DEFAULT_PREFERENCES,
  savePreferences,
  type UserPreferences,
} from "./browser/preferences";
import { BattleRenderer } from "./browser/renderer";
import { runResultMarkup, summarizeRun } from "./browser/run-results";
import { clearSavedRun, saveRunSnapshot } from "./browser/storage";
import { loadProfile, saveProfile } from "./browser/profile-storage";
import {
  localizeEgg,
  localizeItem,
  localizeKnownContentName,
  localizeUpgrade,
  localizeWeapon,
} from "./content-i18n";
import {
  EGG_BY_ID,
  ITEM_BY_ID,
  UPGRADE_BY_ID,
  WEAPON_BY_ID,
} from "./core/content";
import {
  ACHIEVEMENTS,
  completeRun,
  type UnlockReward,
} from "./core/profile";
import {
  buyShopOffer,
  canCombineWeapon,
  canBuyShopOffer,
  chooseUpgrade,
  combineWeapon,
  continueEndless,
  createBattle,
  DEFAULT_BATTLE_CONFIG,
  leaveShop,
  recycleWeapon,
  refreshShop,
  refreshUpgrades,
  getShellBonuses,
  resolveLootReward,
  setPaused,
  shopRefreshCost,
  stepBattle,
  toggleShopOfferLock,
  upgradeRefreshCost,
  weaponRecycleValue,
} from "./core/simulation";
import { challengeTypeForWave } from "./core/waves";
import {
  TICK_SECONDS,
  type BattleConfig,
  type BattleEvent,
  type BattleState,
  type ShellStage,
} from "./core/types";
import {
  applyDocumentLocale,
  isLocale,
  languageOptions,
  t,
  type Locale,
} from "./i18n";

const MAX_FRAME_SECONDS = 0.1;
const INITIAL_ANALYTICS_PROGRESS_TICKS = 5 * 60;
const ANALYTICS_PROGRESS_INTERVAL_TICKS = 30 * 60;

export class EggArenaApp {
  readonly #root: HTMLElement;
  readonly #config: BattleConfig;
  readonly #state: BattleState;
  readonly #runNumber: number;
  #preferences: UserPreferences;
  #locale: Locale;
  readonly #renderer: BattleRenderer;
  readonly #audio: GameAudio;
  readonly #input: InputController;
  readonly #healthBar: HTMLElement;
  readonly #experienceBar: HTMLElement;
  readonly #timer: HTMLElement;
  readonly #level: HTMLElement;
  readonly #materials: HTMLElement;
  readonly #kills: HTMLElement;
  readonly #crates: HTMLElement;
  readonly #shellStage: HTMLElement;
  readonly #toast: HTMLElement;
  readonly #waveLabel: HTMLElement;
  readonly #metaPanel: HTMLElement;
  readonly #weaponRack: HTMLElement;
  readonly #overlay: HTMLElement;
  readonly #overlayTitle: HTMLElement;
  readonly #overlayText: HTMLElement;
  readonly #overlayStandard: HTMLElement;
  readonly #resultPanel: HTMLElement;
  #animationFrame = 0;
  #previousTime = performance.now();
  #accumulator = 0;
  #weaponRackKey = "";
  #metaPanelKey = "";
  #lastSavedTick = -300;
  #lastSavedPhase: BattleState["phase"] | null = null;
  #terminalSaveCleared = false;
  #previousShellStage: ShellStage | null = null;
  #toastTimeout = 0;
  #progressRecorded = false;
  #progressUnlockText = "";
  #play60Tracked = false;
  #abandonTracked = false;
  #nextProgressTick = INITIAL_ANALYTICS_PROGRESS_TICKS;
  #lastProgressReportedSecond = -1;

  constructor(
    root: HTMLElement,
    seed = Date.now(),
    config: BattleConfig = DEFAULT_BATTLE_CONFIG,
    eggId = "fresh-egg",
    restoredState?: BattleState,
    preferences: UserPreferences = DEFAULT_PREFERENCES,
  ) {
    this.#root = root;
    this.#config = config;
    this.#preferences = { ...preferences };
    this.#locale = preferences.locale;
    applyDocumentLocale(this.#locale);
    this.#root.innerHTML = gameMarkup(this.#locale);
    this.#state = restoredState ?? createBattle(seed, config, eggId);
    this.#runNumber = beginAnalyticsRun(restoredState !== undefined);
    this.#play60Tracked = this.#state.tick >= 60 * 60;
    if (this.#state.tick >= INITIAL_ANALYTICS_PROGRESS_TICKS) {
      const completedIntervals = Math.floor(
        (this.#state.tick - INITIAL_ANALYTICS_PROGRESS_TICKS) /
          ANALYTICS_PROGRESS_INTERVAL_TICKS,
      );
      this.#nextProgressTick =
        INITIAL_ANALYTICS_PROGRESS_TICKS +
        (completedIntervals + 1) * ANALYTICS_PROGRESS_INTERVAL_TICKS;
    }
    analytics.track(restoredState ? "run_resume" : "game_start", {
      ...this.#analyticsContext(),
      current_wave: this.#state.wave,
    });
    if (!restoredState) this.#trackWaveStart(this.#state.wave);

    const canvas = required<HTMLCanvasElement>(root, "#game-canvas");
    this.#renderer = new BattleRenderer(canvas, preferences);
    this.#audio = new GameAudio(preferences.masterVolume);
    soundtrack.setVolume(preferences.masterVolume);
    soundtrack.setState("combat");
    this.#healthBar = required(root, "#health-fill");
    this.#experienceBar = required(root, "#experience-fill");
    this.#timer = required(root, "#wave-timer");
    this.#level = required(root, "#player-level");
    this.#materials = required(root, "#materials");
    this.#kills = required(root, "#kills");
    this.#crates = required(root, "#crates");
    this.#shellStage = required(root, "#shell-stage");
    this.#toast = required(root, "#game-toast");
    this.#waveLabel = required(root, "#wave-label");
    this.#metaPanel = required(root, "#meta-panel");
    this.#weaponRack = required(root, "#weapon-rack");
    this.#overlay = required(root, "#game-overlay");
    this.#overlayTitle = required(root, "#overlay-title");
    this.#overlayText = required(root, "#overlay-text");
    this.#overlayStandard = required(root, "#overlay-standard");
    this.#resultPanel = required(root, "#result-panel");
    this.#input = new InputController(() => this.#togglePause());
    this.#input.attachJoystick(required(root, "#joystick"));

    required<HTMLButtonElement>(root, "#resume").addEventListener(
      "click",
      () => this.#togglePause(),
    );
    required<HTMLButtonElement>(root, "#restart").addEventListener(
      "click",
      () => this.#retryRun(),
    );
    required<HTMLButtonElement>(root, "#endless").addEventListener(
      "click",
      () => {
        this.#consumeEvents(continueEndless(this.#state, this.#config));
        this.#terminalSaveCleared = false;
        this.#previousTime = performance.now();
        this.#accumulator = 0;
        this.#renderHud();
      },
    );
    required<HTMLButtonElement>(root, "#pause").addEventListener(
      "click",
      () => this.#togglePause(),
    );
    required<HTMLSelectElement>(root, "#game-language").addEventListener(
      "change",
      this.#onLanguageChange,
    );
    this.#metaPanel.addEventListener("click", this.#onMetaPanelClick);
    this.#overlay.addEventListener("click", this.#onOverlayClick);
    document.addEventListener("visibilitychange", this.#onVisibilityChange);
    window.addEventListener("beforeunload", this.#onPageHide);
    window.addEventListener("pagehide", this.#onPageHide);
    this.#animationFrame = requestAnimationFrame(this.#frame);
  }

  destroy(): void {
    if (this.#state.phase !== "gameover" && this.#state.phase !== "victory") {
      saveRunSnapshot(this.#state, this.#config);
    }
    cancelAnimationFrame(this.#animationFrame);
    this.#input.destroy();
    this.#audio.destroy();
    window.clearTimeout(this.#toastTimeout);
    this.#metaPanel.removeEventListener("click", this.#onMetaPanelClick);
    this.#overlay.removeEventListener("click", this.#onOverlayClick);
    required<HTMLSelectElement>(this.#root, "#game-language").removeEventListener(
      "change",
      this.#onLanguageChange,
    );
    document.removeEventListener("visibilitychange", this.#onVisibilityChange);
    window.removeEventListener("beforeunload", this.#onPageHide);
    window.removeEventListener("pagehide", this.#onPageHide);
  }

  readonly #frame = (time: number): void => {
    const elapsed = Math.min(MAX_FRAME_SECONDS, (time - this.#previousTime) / 1000);
    this.#previousTime = time;
    this.#accumulator += elapsed;

    while (this.#accumulator >= TICK_SECONDS) {
      const events = stepBattle(
        this.#state,
        this.#input.sample(),
        this.#config,
      );
      this.#consumeEvents(events);
      this.#accumulator -= TICK_SECONDS;
      if (!this.#play60Tracked && this.#state.tick >= 60 * 60) {
        this.#play60Tracked = true;
        analytics.track("play_60_seconds", this.#analyticsContext());
      }
      if (this.#state.tick >= this.#nextProgressTick) {
        this.#trackRunProgress("interval");
        this.#nextProgressTick += ANALYTICS_PROGRESS_INTERVAL_TICKS;
      }
      if (this.#state.phase !== "combat") break;
    }

    this.#renderer.render(this.#state);
    this.#renderHud();
    this.#autosave();
    this.#animationFrame = requestAnimationFrame(this.#frame);
  };

  #renderHud(): void {
    const musicState = musicStateForBattle(this.#state);
    if (musicState) soundtrack.setState(musicState);
    const healthRatio = this.#state.player.health / this.#state.player.maxHealth;
    const experienceRatio =
      this.#state.player.experience / this.#state.player.experienceToNext;
    this.#healthBar.style.width = `${Math.max(0, healthRatio) * 100}%`;
    this.#experienceBar.style.width = `${experienceRatio * 100}%`;
    this.#timer.textContent = `${Math.max(0, Math.ceil(this.#state.waveTicksRemaining / 60))}`;
    this.#root.dataset.finalCountdown = `${
      this.#state.phase === "combat" &&
      this.#state.waveTicksRemaining > 0 &&
      this.#state.waveTicksRemaining <= 5 * 60
    }`;
    this.#level.textContent = `LV ${this.#state.player.level}`;
    this.#materials.textContent = `${this.#state.player.materials}`;
    this.#kills.textContent = `${this.#state.player.kills}`;
    this.#crates.textContent = `${this.#state.lootQueue.length + this.#state.pickups.filter((pickup) => pickup.kind === "crate").length}`;
    const shellBonuses = getShellBonuses(this.#state);
    this.#shellStage.textContent =
      shellBonuses.stage === "smooth"
        ? t(this.#locale, "shell.smooth")
        : shellBonuses.stage === "hairline"
          ? t(this.#locale, "shell.hairline")
          : t(this.#locale, "shell.deep");
    this.#root.dataset.shellStage = shellBonuses.stage;
    if (
      this.#previousShellStage !== null &&
      this.#previousShellStage !== shellBonuses.stage
    ) {
      this.#showToast(
        shellBonuses.stage === "smooth"
          ? `${t(this.#locale, "shell.smooth")} · +10 ${t(this.#locale, "stat.armor")}`
          : shellBonuses.stage === "hairline"
            ? `${t(this.#locale, "shell.hairline")} · +12% ${t(this.#locale, "stat.attackSpeed")}`
            : `${t(this.#locale, "shell.deep")} · +25% ${t(this.#locale, "stat.attackSpeed")} · +15% ${t(this.#locale, "stat.damage")}`,
        shellBonuses.stage === "deep",
      );
    }
    this.#previousShellStage = shellBonuses.stage;
    const currentChallenge = challengeTypeForWave(
      this.#state.seed,
      this.#state.dangerLevel,
      this.#state.wave,
    );
    const challengeLabel =
      currentChallenge === "horde"
        ? ` · ${t(this.#locale, "game.horde")}`
        : currentChallenge === "elite"
          ? ` · ${t(this.#locale, "game.elite")}`
          : "";
    this.#waveLabel.textContent = `${t(this.#locale, "game.wave", { wave: String(this.#state.wave).padStart(2, "0") })} · D${this.#state.dangerLevel}${challengeLabel}${this.#state.endless ? " · ∞" : ""}`;
    this.#root.dataset.input = this.#input.owner;
    this.#renderWeaponRack();

    if (this.#state.phase === "paused") {
      this.#metaPanel.hidden = true;
      this.#showOverlay(
        t(this.#locale, "game.paused"),
        t(this.#locale, "game.pausedText"),
        true,
      );
    } else if (this.#state.phase === "levelup") {
      this.#overlay.hidden = true;
      this.#renderLevelUp();
    } else if (this.#state.phase === "loot") {
      this.#overlay.hidden = true;
      this.#renderLoot();
    } else if (this.#state.phase === "shop") {
      this.#overlay.hidden = true;
      this.#renderShop();
    } else if (this.#state.phase === "victory") {
      this.#recordProgress(true);
      this.#metaPanel.hidden = true;
      this.#showResult(true);
    } else if (this.#state.phase === "gameover") {
      this.#recordProgress(false);
      this.#metaPanel.hidden = true;
      this.#showResult(false);
    } else {
      this.#overlay.hidden = true;
      this.#metaPanel.hidden = true;
    }
  }

  #renderWeaponRack(): void {
    const key = `${this.#locale}:` + this.#state.weapons
      .map((weapon) => `${weapon.definitionId}:${weapon.tier}`)
      .join("|");
    if (key === this.#weaponRackKey) return;
    this.#weaponRackKey = key;
    this.#weaponRack.innerHTML = this.#state.weapons
      .map((weapon) => {
        const definition = WEAPON_BY_ID.get(weapon.definitionId);
        const localized = definition
          ? localizeWeapon(this.#locale, definition)
          : { name: weapon.definitionId, description: "" };
        return `<span title="${localized.description}${definition ? ` · ${definition.family.toUpperCase()}` : ""}" style="--weapon-color:${definition?.color ?? "#fff"}">${spriteMarkup("weapon", weapon.definitionId, "rack-icon")}<em>${localized.name}</em><b>${tierLabel(weapon.tier)}</b></span>`;
      })
      .join("");
  }

  #renderLevelUp(): void {
    const key = `${this.#locale}:level:${this.#state.pendingLevelUps}:${this.#state.player.materials}:${this.#state.upgradeRefreshes}:${this.#state.upgradeChoices.map((offer) => `${offer.id}:${offer.tier}`).join(",")}`;
    if (key === this.#metaPanelKey && !this.#metaPanel.hidden) return;
    this.#metaPanelKey = key;
    this.#metaPanel.hidden = false;
    const choices = this.#state.upgradeChoices
      .map((offer) => ({
        offer,
        definition: UPGRADE_BY_ID.get(offer.definitionId),
      }))
      .filter((choice) => choice.definition !== undefined);
    this.#metaPanel.innerHTML = `
      <div class="panel-heading">
        <p class="eyebrow">LEVEL ${this.#state.player.level}</p>
        <h2>${t(this.#locale, "level.choose")}</h2>
        <p>${t(this.#locale, "level.waiting", { count: this.#state.pendingLevelUps })}</p>
      </div>
      <div class="choice-grid upgrade-grid">
        ${choices
          .map(({ offer, definition }, index) => {
            if (!definition) return "";
            const localized = localizeUpgrade(
              this.#locale,
              definition,
              [1, 1.55, 2.25, 3.25][offer.tier - 1],
            );
            return `
              <button class="choice-card tier-${offer.tier}" type="button" data-upgrade="${offer.id}">
                <i class="choice-number">0${index + 1} · T${offer.tier}</i>
                ${spriteMarkup("interface", definition.id, "upgrade-art")}
                <b>${localized.name}</b>
                <span>${localized.description}</span>
                <em>${t(this.#locale, "level.take")}</em>
              </button>`;
          })
          .join("")}
      </div>
      ${this.#buildStatsMarkup()}
      <div class="shop-actions">
        <button class="secondary-button" type="button" data-refresh-upgrade ${this.#state.player.materials < upgradeRefreshCost(this.#state) ? "disabled" : ""}>${t(this.#locale, "shop.refresh", { cost: upgradeRefreshCost(this.#state) })}</button>
      </div>`;
  }

  #renderLoot(): void {
    const reward = this.#state.lootQueue[0];
    if (!reward) return;
    const definition = ITEM_BY_ID.get(reward.definitionId);
    if (!definition) return;
    const key = `${this.#locale}:loot:${reward.id}`;
    if (key === this.#metaPanelKey && !this.#metaPanel.hidden) return;
    this.#metaPanelKey = key;
    this.#metaPanel.hidden = false;
    const localized = localizeItem(this.#locale, definition);
    const recycleValue = Math.max(1, Math.floor(definition.basePrice * 0.25));
    const ownedCount = this.#state.items.filter(
      (itemId) => itemId === definition.id,
    ).length;
    const canTake = ownedCount < (definition.limit ?? Number.POSITIVE_INFINITY);
    this.#metaPanel.innerHTML = `
      <div class="panel-heading">
        <p class="eyebrow">${t(this.#locale, "loot.crate")} · T${reward.tier} · ${this.#state.lootQueue.length} ${this.#locale === "zh-CN" ? "个待处理" : "TO RESOLVE"}</p>
        <div class="loot-item-glyph tier-${reward.tier}">${spriteMarkup("interface", reward.tier >= 4 ? "crate-legendary" : "crate-normal", "loot-crate-art")}${spriteMarkup("item", definition.id, "loot-art")}</div>
        <h2>${localized.name}</h2>
        <p>${localized.description}</p>
      </div>
      <div class="choice-grid upgrade-grid loot-grid">
        <button class="choice-card" type="button" data-loot-take="${reward.id}" ${canTake ? "" : "disabled"}>
          <b>${t(this.#locale, "loot.take")}</b><span>${t(this.#locale, canTake ? "loot.takeDescription" : "loot.limitReached")}</span>
        </button>
        <button class="choice-card" type="button" data-loot-recycle="${reward.id}">
          <b>${t(this.#locale, "shop.recycle", { value: recycleValue })}</b><span>${t(this.#locale, "loot.recycleDescription")}</span>
        </button>
      </div>`;
  }

  #renderShop(): void {
    const key = `${this.#locale}:shop:${this.#state.wave}:${this.#state.player.materials}:${this.#state.shopRefreshes}:${this.#state.shopOffers
      .map((offer) => `${offer.id}:${offer.sold}:${offer.locked}`)
      .join(",")}:${this.#state.weapons
      .map((weapon) => `${weapon.definitionId}:${weapon.tier}`)
      .join(",")}`;
    if (key === this.#metaPanelKey && !this.#metaPanel.hidden) return;
    this.#metaPanelKey = key;
    this.#metaPanel.hidden = false;
    const nextChallenge = challengeTypeForWave(
      this.#state.seed,
      this.#state.dangerLevel,
      this.#state.wave + 1,
    );
    const nextChallengeLabel =
      nextChallenge === "horde"
        ? t(this.#locale, "game.horde")
        : nextChallenge === "elite"
          ? t(this.#locale, "game.elite")
          : "";
    this.#metaPanel.innerHTML = `
      <div class="panel-heading shop-heading">
        <div>
          <p class="eyebrow">${t(this.#locale, "shop.waveCleared", { wave: String(this.#state.wave).padStart(2, "0") })}</p>
          <h2>${t(this.#locale, "shop.title")}</h2>
          <p>${t(this.#locale, "shop.description")}</p>
        </div>
        <div class="shop-wallet"><i>◆</i><b>${this.#state.player.materials}</b></div>
      </div>
      <div class="choice-grid shop-grid">
        ${this.#state.shopOffers
          .map((offer) => {
            let localized = { name: offer.definitionId, description: "" };
            let familyLabel = "";
            if (offer.kind === "weapon") {
              const weapon = WEAPON_BY_ID.get(offer.definitionId);
              if (weapon) {
                localized = localizeWeapon(this.#locale, weapon);
                familyLabel = ` · ${weapon.family.toUpperCase()}`;
              }
            } else {
              const item = ITEM_BY_ID.get(offer.definitionId);
              if (item) localized = localizeItem(this.#locale, item);
            }
            const affordable = this.#state.player.materials >= offer.price;
            const purchasable = canBuyShopOffer(this.#state, offer.id);
            const rackBlocked =
              offer.kind === "weapon" &&
              affordable &&
              !offer.sold &&
              !purchasable;
            return `
              <article class="choice-card shop-card tier-${offer.tier} ${offer.sold ? "is-sold" : ""} ${offer.locked ? "is-locked" : ""}" data-sold-label="${t(this.#locale, "shop.sold")}">
                <i class="offer-kind">${t(this.#locale, offer.kind === "weapon" ? "shop.weapon" : "shop.item")}${familyLabel} · T${offer.tier}</i>
                ${spriteMarkup(offer.kind, offer.definitionId, "shop-offer-art")}
                <b>${localized.name}</b>
                <span>${localized.description}</span>
                <div class="offer-actions">
                  <button type="button" data-buy="${offer.id}" ${!purchasable ? "disabled" : ""}>${rackBlocked ? t(this.#locale, "shop.rackFull") : `<i>◆</i> ${offer.price}`}</button>
                  <button class="lock-button" type="button" data-lock="${offer.id}" ${offer.sold ? "disabled" : ""}>${offer.locked ? t(this.#locale, "shop.locked") : t(this.#locale, "shop.lock")}</button>
                </div>
              </article>`;
          })
          .join("")}
      </div>
      ${this.#buildStatsMarkup()}
      <section class="shop-loadout" aria-label="${t(this.#locale, "game.equipped")}">
        <div>
          <p class="eyebrow">${t(this.#locale, "shop.equipped")}</p>
          <span>${t(this.#locale, "shop.loadoutDescription")}</span>
        </div>
        <div class="recycle-list">
          ${this.#state.weapons
            .map((weapon, index) => {
              const definition = WEAPON_BY_ID.get(weapon.definitionId);
              const localized = definition
                ? localizeWeapon(this.#locale, definition)
                : { name: weapon.definitionId };
              const lastWeapon = this.#state.weapons.length <= 1;
              const canCombine = canCombineWeapon(this.#state, index);
              return `
                <article class="loadout-weapon ${canCombine ? "can-combine" : ""}">
                  ${spriteMarkup("weapon", weapon.definitionId, "recycle-weapon-art")}
                  <span>${localized.name} · ${tierLabel(weapon.tier)}</span>
                  <div class="loadout-actions">
                    <button class="combine-button" type="button" data-combine="${index}" ${canCombine ? "" : "disabled"}>${t(this.#locale, "shop.combine")}</button>
                    <button type="button" data-recycle="${index}" ${lastWeapon ? "disabled" : ""}>${lastWeapon ? t(this.#locale, "shop.keepOne") : t(this.#locale, "shop.recycle", { value: weaponRecycleValue(weapon) })}</button>
                  </div>
                </article>`;
            })
            .join("")}
        </div>
      </section>
      <div class="shop-actions">
        ${nextChallenge ? `<strong class="challenge-warning">${t(this.#locale, "shop.nextChallenge", { type: nextChallengeLabel, wave: String(this.#state.wave + 1) })}</strong>` : ""}
        <button class="secondary-button" type="button" data-refresh ${this.#state.player.materials < shopRefreshCost(this.#state) ? "disabled" : ""}>${t(this.#locale, "shop.refresh", { cost: shopRefreshCost(this.#state) })}</button>
        <button class="primary compact-primary" type="button" data-next-wave>${this.#state.wave >= 20 && !this.#state.endless ? t(this.#locale, "shop.claimVictory") : t(this.#locale, "shop.startWave", { wave: String(this.#state.wave + 1).padStart(2, "0") })}</button>
      </div>`;
  }

  #buildStatsMarkup(): string {
    const player = this.#state.player;
    const zh = this.#locale === "zh-CN";
    const stats = [
      [zh ? "最大蛋壳" : "MAX SHELL", player.maxHealth],
      [zh ? "生命恢复" : "REGEN", player.regenerationPerSecond],
      [zh ? "生命偷取" : "LIFE STEAL", `${Math.round(player.lifeStealChance * 100)}%`],
      [zh ? "伤害" : "DAMAGE", `${Math.round((player.damageMultiplier - 1) * 100)}%`],
      [zh ? "近战" : "MELEE", player.meleeDamage],
      [zh ? "远程" : "RANGED", player.rangedDamage],
      [zh ? "元素" : "ELEMENTAL", player.elementalDamage],
      [zh ? "攻速" : "ATTACK SPEED", `${Math.round((player.attackSpeedMultiplier - 1) * 100)}%`],
      [zh ? "暴击" : "CRIT", `${Math.round(player.critChance * 100)}%`],
      [zh ? "射程" : "RANGE", player.range],
      [zh ? "护甲" : "ARMOR", player.armor],
      [zh ? "闪避" : "DODGE", `${Math.round(player.dodgeChance * 100)}%`],
      [zh ? "速度" : "SPEED", Math.round(player.speed)],
      [zh ? "幸运" : "LUCK", player.luck],
      [zh ? "收获" : "HARVEST", Math.round(player.harvesting)],
      [zh ? "工程" : "ENGINEERING", player.engineering],
    ];
    return `
      <details class="build-stats">
        <summary>${zh ? "当前属性" : "CURRENT BUILD STATS"}</summary>
        <div>${stats.map(([label, value]) => `<span><i>${label}</i><b>${value}</b></span>`).join("")}</div>
      </details>`;
  }

  #showOverlay(
    title: string,
    text: string,
    canResume: boolean,
    canContinueEndless = false,
  ): void {
    this.#overlay.hidden = false;
    this.#overlayStandard.hidden = false;
    this.#resultPanel.hidden = true;
    this.#overlayTitle.textContent = title;
    this.#overlayText.textContent = text;
    required<HTMLButtonElement>(this.#root, "#resume").hidden = !canResume;
    required<HTMLButtonElement>(this.#root, "#restart").hidden = canResume;
    required<HTMLButtonElement>(this.#root, "#endless").hidden =
      !canContinueEndless;
  }

  #showResult(victory: boolean): void {
    const key = `${this.#locale}:${victory}:${this.#state.tick}:${this.#progressUnlockText}`;
    this.#overlay.hidden = false;
    this.#overlayStandard.hidden = true;
    this.#resultPanel.hidden = false;
    if (this.#resultPanel.dataset.resultKey === key) return;
    this.#resultPanel.dataset.resultKey = key;
    this.#resultPanel.innerHTML = runResultMarkup(
      this.#locale,
      this.#state,
      victory,
      this.#progressUnlockText,
    );
  }

  #retryRun(): void {
    analytics.track("game_restart", {
      ...this.#analyticsContext(),
      current_wave: this.#state.wave,
      next_run_number: this.#runNumber + 1,
    });
    const config = {
      ...this.#config,
      startingWeaponId:
        this.#config.startingWeaponId ??
        EGG_BY_ID.get(this.#state.eggId)?.startingWeaponId,
    };
    const eggId = this.#state.eggId;
    const preferences = { ...this.#preferences };
    this.destroy();
    clearSavedRun();
    new EggArenaApp(
      this.#root,
      Date.now(),
      config,
      eggId,
      undefined,
      preferences,
    );
  }

  #togglePause(): void {
    if (this.#state.phase === "combat") {
      this.#consumeEvents(setPaused(this.#state, true));
    } else if (this.#state.phase === "paused") {
      this.#previousTime = performance.now();
      this.#accumulator = 0;
      this.#consumeEvents(setPaused(this.#state, false));
    }
  }

  #consumeEvents(events: BattleEvent[]): void {
    this.#renderer.consume(events);
    this.#audio.consume(events);
    for (const event of events) {
      if (event.type === "boss-spawned") {
        this.#showToast(
          t(this.#locale, "event.boss", {
            name: localizeKnownContentName(this.#locale, event.name),
          }),
          true,
        );
      } else if (event.type === "weapon-merged") {
        this.#showToast(
          t(this.#locale, "event.merged", {
            name: localizeKnownContentName(this.#locale, event.name),
            tier: event.tier,
          }),
        );
      } else if (event.type === "purchase") {
        this.#showToast(
          t(this.#locale, "event.added", {
            name: localizeKnownContentName(this.#locale, event.name),
          }),
        );
      } else if (event.type === "weapon-recycled") {
        this.#showToast(
          t(this.#locale, "event.recycled", {
            name: localizeKnownContentName(this.#locale, event.name),
            value: event.value,
          }),
        );
      } else if (event.type === "level-up") {
        this.#showToast(
          t(this.#locale, "event.level", { level: event.level }),
        );
      } else if (event.type === "wave-start") {
        this.#trackWaveStart(event.wave);
        this.#showToast(
          t(this.#locale, "event.wave", {
            wave: String(event.wave).padStart(2, "0"),
          }),
        );
      } else if (event.type === "wave-cleared") {
        analytics.track("level_end", {
          ...this.#analyticsContext(),
          level_name: `wave_${event.wave}`,
          success: 1,
          wave: event.wave,
        });
        this.#showToast(
          this.#locale === "zh-CN"
            ? `第 ${String(event.wave).padStart(2, "0")} 波完成`
            : `WAVE ${String(event.wave).padStart(2, "0")} CLEARED`,
        );
      }
    }
  }

  #showToast(message: string, danger = false): void {
    window.clearTimeout(this.#toastTimeout);
    this.#toast.textContent = message;
    this.#toast.dataset.danger = `${danger}`;
    this.#toast.hidden = false;
    this.#toastTimeout = window.setTimeout(() => {
      this.#toast.hidden = true;
    }, 2_300);
  }

  #recordProgress(victory: boolean): void {
    if (this.#progressRecorded) return;
    this.#progressRecorded = true;
    const summary = summarizeRun(this.#state, victory);
    const resultParams = {
      ...this.#analyticsContext(),
      duration_seconds: summary.durationSeconds,
      item_count: summary.itemCount,
      kills: summary.kills,
      materials_collected: summary.materials,
      outcome: summary.outcome,
      player_level: summary.level,
      wave_reached: summary.wave,
      weapon_count: summary.weaponCount,
    };
    analytics.track("game_over", resultParams);
    if (victory) analytics.track("game_complete", resultParams);
    const update = completeRun(loadProfile(), this.#state, victory);
    saveProfile(update.profile);
    if (!update.recorded) return;
    for (const achievementId of update.newAchievementIds) {
      analytics.track("unlock_achievement", {
        ...this.#analyticsContext(),
        achievement_id: achievementId,
      });
    }
    const achievementNames = update.newAchievementIds
      .map((id) => ACHIEVEMENTS.find((entry) => entry.id === id))
      .filter((entry) => entry !== undefined)
      .map((entry) => this.#locale === "zh-CN" ? entry.titleZh : entry.title);
    const rewardNames = update.rewards.flatMap((reward) =>
      this.#localizedRewardNames(reward),
    );
    const messages: string[] = [];
    if (achievementNames.length > 0) {
      messages.push(
        this.#locale === "zh-CN"
          ? `成就达成：${achievementNames.join("、")}`
          : `Achievements: ${achievementNames.join(", ")}`,
      );
    }
    if (rewardNames.length > 0) {
      messages.push(
        this.#locale === "zh-CN"
          ? `新解锁：${rewardNames.join("、")}`
          : `Unlocked: ${rewardNames.join(", ")}`,
      );
    }
    if (update.unlockedDangerLevel) {
      messages.push(
        this.#locale === "zh-CN"
          ? `危险 ${update.profile.unlockedDangerLevel} 已解锁`
          : `Danger ${update.profile.unlockedDangerLevel} unlocked`,
      );
    }
    this.#progressUnlockText = messages.length > 0 ? ` · ${messages.join(" · ")}` : "";
  }

  #analyticsContext(extra: AnalyticsParams = {}): AnalyticsParams {
    return {
      character: this.#state.eggId,
      danger_level: this.#state.dangerLevel,
      game_mode: this.#state.endless ? "endless" : "standard",
      run_number: this.#runNumber,
      starting_weapon:
        this.#config.startingWeaponId ??
        EGG_BY_ID.get(this.#state.eggId)?.startingWeaponId ??
        "unknown",
      ...extra,
    };
  }

  #trackWaveStart(wave: number): void {
    analytics.track("level_start", {
      ...this.#analyticsContext(),
      level_name: `wave_${wave}`,
      wave,
    });
  }

  #localizedRewardNames(reward: UnlockReward): string[] {
    const names: string[] = [];
    const egg = reward.eggId ? EGG_BY_ID.get(reward.eggId) : undefined;
    const weapon = reward.weaponId
      ? WEAPON_BY_ID.get(reward.weaponId)
      : undefined;
    const item = reward.itemId ? ITEM_BY_ID.get(reward.itemId) : undefined;
    if (egg) names.push(localizeEgg(this.#locale, egg).name);
    if (weapon) names.push(localizeWeapon(this.#locale, weapon).name);
    if (item) names.push(localizeItem(this.#locale, item).name);
    return names;
  }

  #autosave(): void {
    if (this.#state.phase === "gameover" || this.#state.phase === "victory") {
      if (!this.#terminalSaveCleared) {
        clearSavedRun();
        this.#terminalSaveCleared = true;
      }
      return;
    }

    const phaseChanged = this.#lastSavedPhase !== this.#state.phase;
    const intervalElapsed = this.#state.tick - this.#lastSavedTick >= 300;
    if (!phaseChanged && !intervalElapsed) return;
    if (saveRunSnapshot(this.#state, this.#config)) {
      this.#lastSavedTick = this.#state.tick;
      this.#lastSavedPhase = this.#state.phase;
    }
  }

  readonly #onVisibilityChange = (): void => {
    if (!document.hidden) return;
    this.#trackRunProgress("visibility_hidden");
    if (this.#state.phase === "combat") this.#togglePause();
  };

  readonly #onPageHide = (event: Event): void => {
    if (
      this.#abandonTracked ||
      this.#progressRecorded ||
      this.#state.phase === "gameover" ||
      this.#state.phase === "victory"
    ) return;

    this.#abandonTracked = true;
    this.#trackRunProgress(event.type);
    const summary = summarizeRun(this.#state, false);
    analytics.track("run_abandon", {
      ...this.#analyticsContext(),
      duration_seconds: summary.durationSeconds,
      exit_reason: event.type,
      kills: summary.kills,
      materials_collected: summary.materials,
      player_level: summary.level,
      transport_type: "beacon",
      wave_reached: summary.wave,
      weapon_count: summary.weaponCount,
    });
  };

  #trackRunProgress(reason: string): void {
    const summary = summarizeRun(this.#state, false);
    if (summary.durationSeconds === this.#lastProgressReportedSecond) return;
    this.#lastProgressReportedSecond = summary.durationSeconds;
    analytics.track("run_progress", {
      ...this.#analyticsContext(),
      checkpoint_reason: reason,
      duration_seconds: summary.durationSeconds,
      kills: summary.kills,
      materials_collected: summary.materials,
      player_level: summary.level,
      wave_reached: summary.wave,
      weapon_count: summary.weaponCount,
    });
  }

  readonly #onOverlayClick = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest("[data-result-retry]")) {
      this.#retryRun();
      return;
    }
    if (target.closest("[data-result-endless]")) {
      this.#consumeEvents(continueEndless(this.#state, this.#config));
      this.#terminalSaveCleared = false;
      this.#previousTime = performance.now();
      this.#accumulator = 0;
      this.#renderHud();
      return;
    }
    if (target.closest("[data-result-title], [data-result-new]")) {
      window.location.reload();
    }
  };

  readonly #onLanguageChange = (event: Event): void => {
    const locale = (event.target as HTMLSelectElement).value;
    if (!isLocale(locale)) return;
    this.#locale = locale;
    this.#renderer.setLocale(locale);
    this.#preferences = { ...this.#preferences, locale };
    savePreferences(this.#preferences);
    applyDocumentLocale(locale);
    document.title = `${t(locale, "home.title")}: ${t(locale, "home.subtitle")}`;
    this.#weaponRackKey = "";
    this.#metaPanelKey = "";
    this.#applyStaticLocale();
    this.#renderHud();
  };

  #applyStaticLocale(): void {
    const locale = this.#locale;
    required(this.#root, ".game-shell").setAttribute(
      "aria-label",
      t(locale, "game.aria"),
    );
    required(this.#root, "#game-language").setAttribute(
      "aria-label",
      t(locale, "language.label"),
    );
    required(this.#root, "#pause").setAttribute(
      "aria-label",
      t(locale, "game.pause"),
    );
    required(this.#root, "#game-canvas").setAttribute(
      "aria-label",
      t(locale, "game.arena"),
    );
    required(this.#root, "#shell-label").textContent = t(
      locale,
      "shell.label",
    );
    required(this.#root, ".material-pill").setAttribute(
      "title",
      t(locale, "game.materials"),
    );
    required(this.#root, ".kill-pill").setAttribute(
      "title",
      t(locale, "game.eliminations"),
    );
    required(this.#root, ".crate-pill").setAttribute(
      "title",
      t(locale, "loot.crate"),
    );
    required(this.#root, "#joystick").setAttribute(
      "aria-label",
      t(locale, "game.joystick"),
    );
    required(this.#root, "#overlay-eyebrow").textContent = t(
      locale,
      "game.runStatus",
    );
    required(this.#root, "#resume").textContent = t(locale, "game.resume");
    required(this.#root, "#endless").textContent = t(locale, "game.endless");
    required(this.#root, "#restart").textContent = t(locale, "game.again");
    required(this.#root, "#weapon-rack").setAttribute(
      "aria-label",
      t(locale, "game.equipped"),
    );
    required(this.#root, "#help-move-label").textContent = t(
      locale,
      "game.move",
    );
    required(this.#root, "#help-move-key").textContent = t(
      locale,
      "game.arrows",
    );
    required(this.#root, "#help-fire-label").textContent = t(
      locale,
      "game.autoFire",
    );
    required(this.#root, "#help-fire-state").textContent = t(
      locale,
      "game.on",
    );
    required(this.#root, "#help-pause-label").textContent = t(
      locale,
      "game.pause",
    );
  }

  readonly #onMetaPanelClick = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const upgradeButton = target.closest<HTMLElement>("[data-upgrade]");
    if (upgradeButton?.dataset.upgrade) {
      this.#consumeEvents(
        chooseUpgrade(this.#state, Number(upgradeButton.dataset.upgrade)),
      );
      this.#renderHud();
      return;
    }

    const takeLootButton = target.closest<HTMLElement>("[data-loot-take]");
    if (takeLootButton?.dataset.lootTake) {
      this.#consumeEvents(
        resolveLootReward(
          this.#state,
          Number(takeLootButton.dataset.lootTake),
          true,
        ),
      );
      this.#renderHud();
      return;
    }

    const recycleLootButton = target.closest<HTMLElement>(
      "[data-loot-recycle]",
    );
    if (recycleLootButton?.dataset.lootRecycle) {
      this.#consumeEvents(
        resolveLootReward(
          this.#state,
          Number(recycleLootButton.dataset.lootRecycle),
          false,
        ),
      );
      this.#renderHud();
      return;
    }

    const buyButton = target.closest<HTMLElement>("[data-buy]");
    if (buyButton?.dataset.buy) {
      this.#consumeEvents(
        buyShopOffer(this.#state, Number(buyButton.dataset.buy)),
      );
      this.#renderHud();
      return;
    }

    const recycleButton = target.closest<HTMLElement>("[data-recycle]");
    if (recycleButton?.dataset.recycle) {
      this.#consumeEvents(
        recycleWeapon(this.#state, Number(recycleButton.dataset.recycle)),
      );
      this.#renderHud();
      return;
    }

    const combineButton = target.closest<HTMLElement>("[data-combine]");
    if (combineButton?.dataset.combine) {
      this.#consumeEvents(
        combineWeapon(this.#state, Number(combineButton.dataset.combine)),
      );
      this.#renderHud();
      return;
    }

    const lockButton = target.closest<HTMLElement>("[data-lock]");
    if (lockButton?.dataset.lock) {
      toggleShopOfferLock(this.#state, Number(lockButton.dataset.lock));
      this.#renderShop();
      return;
    }

    if (target.closest("[data-refresh]")) {
      refreshShop(this.#state);
      this.#renderShop();
      return;
    }

    if (target.closest("[data-refresh-upgrade]")) {
      refreshUpgrades(this.#state);
      this.#renderLevelUp();
      return;
    }

    if (target.closest("[data-next-wave]")) {
      this.#previousTime = performance.now();
      this.#accumulator = 0;
      this.#consumeEvents(leaveShop(this.#state, this.#config));
      this.#renderHud();
    }
  };
}

function gameMarkup(locale: Locale): string {
  return `
    <main class="game-shell" aria-label="${t(locale, "game.aria")}">
      <header class="game-hud">
        <div class="hud-brand"><b>EGGATO</b><span>SHELLSTORM</span></div>
        <div class="wave-counter"><span id="wave-label">${t(locale, "game.wave", { wave: "01" })}</span><b id="wave-timer">30</b></div>
        <div class="hud-actions">
          <label class="language-picker game-language-picker">
            <span>${t(locale, "language.label")}</span>
            <select id="game-language" aria-label="${t(locale, "language.label")}">${languageOptions(locale)}</select>
          </label>
          <button id="pause" class="icon-button" type="button" aria-label="${t(locale, "game.pause")}">Ⅱ</button>
        </div>
      </header>
      <section class="battle-frame">
        <canvas id="game-canvas" aria-label="${t(locale, "game.arena")}"></canvas>
        <div class="combat-stats" aria-live="polite">
          <div class="health-row">
            <span><b id="shell-stage">${t(locale, "shell.smooth")}</b> <i id="shell-label">${t(locale, "shell.label")}</i></span>
            <div class="meter health-meter"><i id="health-fill"></i></div>
          </div>
          <div class="experience-row">
            <b id="player-level">LV 1</b>
            <div class="meter experience-meter"><i id="experience-fill"></i></div>
          </div>
        </div>
        <div class="resource-pill material-pill" title="${t(locale, "game.materials")}">${spriteMarkup("interface", "material", "hud-resource-art")}<b id="materials">0</b></div>
        <div class="resource-pill crate-pill" title="${t(locale, "loot.crate")}">${spriteMarkup("interface", "crate-normal", "hud-resource-art")}<b id="crates">0</b></div>
        <div class="resource-pill kill-pill" title="${t(locale, "game.eliminations")}"><i>✦</i><b id="kills">0</b></div>
        <div id="game-toast" class="game-toast" role="status" aria-live="polite" hidden></div>
        <div id="joystick" class="joystick" data-active="false" aria-label="${t(locale, "game.joystick")}"><i></i></div>
        <section id="meta-panel" class="meta-panel" hidden></section>
        <section id="game-overlay" class="game-overlay" hidden>
          <div id="overlay-standard">
            <p id="overlay-eyebrow" class="eyebrow">${t(locale, "game.runStatus")}</p>
            <h2 id="overlay-title">${t(locale, "game.paused")}</h2>
            <p id="overlay-text"></p>
            <button id="resume" class="primary" type="button">${t(locale, "game.resume")}</button>
            <button id="endless" class="secondary-button endless-button" type="button" hidden>${t(locale, "game.endless")}</button>
            <button id="restart" class="primary" type="button" hidden>${t(locale, "game.again")}</button>
          </div>
          <section id="result-panel" hidden></section>
        </section>
      </section>
      <aside id="weapon-rack" class="weapon-rack" aria-label="${t(locale, "game.equipped")}"></aside>
      <footer class="game-help">
        <span><i id="help-move-label">${t(locale, "game.move")}</i> <b id="help-move-key">${t(locale, "game.arrows")}</b></span>
        <span><i id="help-fire-label">${t(locale, "game.autoFire")}</i> <b id="help-fire-state">${t(locale, "game.on")}</b></span>
        <span><i id="help-pause-label">${t(locale, "game.pause")}</i> <b>P / ESC</b></span>
      </footer>
    </main>
  `;
}

function tierLabel(tier: number): string {
  return ["I", "II", "III", "IV"][tier - 1] ?? `T${tier}`;
}

function required<T extends Element = HTMLElement>(
  root: ParentNode,
  selector: string,
): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Required element is missing: ${selector}`);
  return element;
}
